import { Injectable, inject, signal } from '@angular/core';
import { Observable, map, of, shareReplay } from 'rxjs';

import { ApiService } from '../../shared/services/api.service';
import { DateRange, Peer, StateService } from './state.service';
import { InstitutionType } from './institution.service';
import { HttpParams } from '@angular/common/http';

export interface Metric {
  metricId: string;
  description?: string;
  description1?: string;
  description2?: string;
  narrative?: string;
  title?: string;
  alternateTitle?: string;
  alternateTitle1?: string;
  alternateTitle2?: string;
  displayType: string;
  rankSort: string;
}

export interface DecayMetricData {
  metricId: string;
  iid: number;
  reportDate: Date;
  value?: number;
  standardAverageBalanceVolatile: number;
  growthRate: number;
}

export interface ElementMetric {
  metricId: string;
  displayType: string;
  compType: string;
  dataType: string;
  rowTitle: string;
  displayOrder: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any[];
}

export interface ElementMetricMax {
  reportDate: Date;
  metricId: string;
  value?: number;
}

export interface TrendMetric {
  categoryTitle: string;
  categoryDisplayOrder: number;
  categoryInformation?: string;
  metricId: string;
  metricTitle: string;
  displayType: string;
  metricDisplayOrder: number;
  metricInformation?: string;
}

export interface DecayMetric {
  categoryTitle: string;
  categoryDisplayOrder: number;
  categoryInformation?: string;
  metricId: string;
  metricTitle: string;
  displayType: string;
  metricDisplayOrder: number;
  metricInformation?: string;
}

export interface CorrelationMetric {
  metricId: string;
  alternateTitle: string;
  category: string;
  displayType: string;
  correlationsDefault: boolean;
  rates: CorrelationRate[];
}

export interface CorrelationRate {
  rateId: string;
  rateName: string;
  category: string;
  correlationsDefault: boolean;
}

export interface InstitutionStatistical {
  iid: number;
  metricId: string;
  alternateTitle: string;
  rateId: string;
  rateName: string;
  beta?: number;
  rSquared?: number;
}

export interface QuarterlyGroup {
  reportDate: Date;
  sectionTitle: string;
  sectionInformation: string;
  sectionOrder: number;
  metricId: string;
  rowTitle: string;
  rowInformation?: string;
  level: number;
  rowOrder: number;
  displayType: string;
  rankSort: string;
  institutionValue?: number;
  institutionRank?: number;
  groupValue?: number;
  difference: number;
  topInstitutionIid?: number;
  topInstitutionLabel?: string;
  topInstitutionValue?: number;
  institutionsReporting: number;
  details?: QuarterlyGroupDetail[];
}

export interface QuarterlyGroupDetail {
  iid: number;
  label: string;
  value: number;
  rank?: number;
}

export interface QuarterlyInstitution {
  reportDate: Date;
  sectionTitle: string;
  sectionInformation: string;
  sectionOrder: number;
  metricId: string;
  rowTitle: string;
  rowInformation?: string;
  level: number;
  rowOrder: number;
  displayType: string;
  rankSort: string;
  institutionIid: number;
  institutionValue: number;
  details?: QuarterlyInstitutionDetail[];
  isSelected: boolean;
}

export interface QuarterlyInstitutionDetail {
  iid: number;
  value: number;
  difference: number;
}

export interface ChartElement {
  id: string;
  title: string;
  sort: number;
  plot: boolean;
  value: number;
}

@Injectable({
  providedIn: 'root'
})
export class MetricService {
  private apiService = inject(ApiService);

  readonly institution = inject(StateService).institution;
  private trendMetricsMap = new Map<InstitutionType, Observable<TrendMetric[]>>();
  private decayMetricsMap = new Map<InstitutionType, Observable<DecayMetric[]>>();
  private correlationMetricsMap = new Map<InstitutionType, Observable<CorrelationMetric[]>>();
  selectedDecayMetric = signal<DecayMetric | undefined>(undefined);

  // Method to update the selected metric
  setSelecteDecaydMetric(metric: DecayMetric): void {
    this.selectedDecayMetric.set(metric);
  }

  // Method to get the selected metric
  getSelectedDecayMetric() {
    return this.selectedDecayMetric();
  }

  private cache = new Map<string, Observable<any>>();

  public getTrendMetrics(institutionType: InstitutionType): Observable<TrendMetric[]> {
    if (!this.trendMetricsMap.has(institutionType)) {
      this.trendMetricsMap.set(institutionType, this.apiService.get<TrendMetric[]>(`/api/${institutionType}/metric/trend`)
        .pipe(shareReplay(1)));
    }
    return this.trendMetricsMap.get(institutionType) ?? of([]);
  }

  public getDecayMetrics(institutionType: InstitutionType): Observable<DecayMetric[]> {
    if (!this.decayMetricsMap.has(institutionType)) {
      this.decayMetricsMap.set(institutionType, this.apiService.get<DecayMetric[]>(`/api/${institutionType}/metric/decay`)
        .pipe(shareReplay(1)));
    }
    return this.decayMetricsMap.get(institutionType) ?? of([]);
  }

  public getCorrelationMetrics(institutionType: InstitutionType): Observable<CorrelationMetric[]> {
    if (!this.correlationMetricsMap.has(institutionType)) {
      this.correlationMetricsMap.set(institutionType, this.apiService.get<CorrelationMetric[]>(`/api/${institutionType}/metric/correlation`)
        .pipe(shareReplay(1)));
    }
    return this.correlationMetricsMap.get(institutionType) ?? of([]);
  }

  public getMetric(iid: number, metricId: string): Observable<Metric> {
    const key = `${iid}-${metricId}`;
    if (!this.cache.has(key)) {
      this.cache.set(key, this.apiService.get<Metric>(`/api/${iid}/metric/${metricId}`)
        .pipe(shareReplay(1)));
    }
    return this.cache.get(key) ?? of({} as Metric);
  }

  public getDecayMetricData(iid: number, metricId: string): Observable<DecayMetricData[]> {
    const key = `${iid}-${metricId}`;
    if (!this.cache.has(key)) {
      this.cache.set(key, this.apiService.get<DecayMetricData[]>(`/api/${iid}/metric/decay/${metricId}`)
        .pipe(shareReplay(1)));
    }
    return this.cache.get(key) ?? of({} as DecayMetricData[]);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public getMetricData(iid: number, metricId: string, peer?: Peer, dateKey?: string): Observable<any> {
    const key = `${iid}-${metricId}-${peer?.institution?.iid ?? peer?.group?.groupId ?? ''}/${dateKey}`;
    const type = peer?.institution?.iid ? 'iid' : peer?.group ? 'group' : 'none';
    if (!this.cache.has(key)) {

      let params = new HttpParams();
      if (dateKey !== undefined && dateKey !== null) {
        params = params.set('dateKey', dateKey);
      }
      this.cache.set(key, this.apiService.get<any>(
        `/api/${iid}/metric/${metricId}/${type}/${peer?.institution?.iid ?? peer?.group?.groupId ?? ''}`,
        { params }
      ).pipe(shareReplay(1)));
    }
    return this.cache.get(key) ?? of([]);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public getMetricInstitutionsData(iid: number, metricId: string, iids?: number[]): Observable<any> {
    const key = `${iid}-${metricId}-${iids?.join(',')}`;
    const type = 'iids';
    if (!this.cache.has(key)) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.cache.set(key, this.apiService.get<any>(`/api/${iid}/metric/${metricId}/${type}/${iids?.join(',')}`)
        .pipe(shareReplay(1)));
    }
    return this.cache.get(key) ?? of([]);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public getMetricPercentileData(iid: number, metricId: string, groupId: string, iids: number[]): Observable<any> {
    const key = `${iid}-${metricId}-${groupId}-${iids.join(',')}`;
    if (!this.cache.has(key)) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.cache.set(key, this.apiService.get<any>(`/api/${iid}/metric/percentile/${metricId}/${groupId}/${iids.join(',')}`)
        .pipe(shareReplay(1)));
    }
    return this.cache.get(key) ?? of([]);
  }

  public getElementData(iid: number, elementId: string, peer?: Peer): Observable<ElementMetric[]> {
    const key = `${iid}-${elementId}-${peer?.institution?.iid ?? peer?.group?.groupId}`;
    const type = peer?.institution?.iid ? 'iid' : peer?.group ? 'group' : 'none';
    if (!this.cache.has(key)) {
      this.cache.set(key, this.apiService.get<ElementMetric[]>(`/api/${iid}/element/${elementId}/${type}/${peer?.institution?.iid ?? peer?.group?.groupId ?? ''}`
      )
        .pipe(shareReplay(1)));
    }
    return this.cache.get(key) ?? of([]);
  }

  public getElementDataMax(iid: number, elementId: string, peer?: Peer): Observable<ElementMetricMax[]> {
    const key = `${iid}-max-${elementId}-${peer?.institution?.iid ?? peer?.group?.groupId}`;
    const type = peer?.institution?.iid ? 'iid' : peer?.group ? 'group' : 'none';
    if (!this.cache.has(key)) {
      this.cache.set(key, this.apiService.get<ElementMetricMax[]>(`/api/${iid}/element/max/${elementId}/${type}/${peer?.institution?.iid ?? peer?.group?.groupId ?? ''}`)
        .pipe(shareReplay(1)));
    }
    return this.cache.get(key) ?? of([]);
  }

  public getChartElements(elementId: string, date: Date, sort: string, numElements: number): Observable<ChartElement[]> {
    return this.getElementData(this.institution().iid, elementId)
      .pipe(map((metrics) => {
        const chartElements: ChartElement[] = [];
        let index = 0;

        metrics.forEach((metric) => {
          const dataRow = metric.data.findIndex((r) => { return new Date(r[0]).getTime() == date.getTime(); });
          chartElements.push({
            id: metric.metricId,
            title: metric.rowTitle,
            sort: index++,
            plot: false,
            value: dataRow > -1 ? metric.data[dataRow][1] : 0
          });
        });

        if (sort == 'amount') {
          chartElements.sort((a: { value: number }, b: { value: number }) => (a.value < b.value) ? 1 : -1);
        }

        chartElements.slice(0, numElements).forEach((element) => element.plot = true);
        return chartElements;
      }));
  }

  public getInstitutionStatisticals(iid: number, metricId: string, dateRange: DateRange, currentProduct?: string): Observable<InstitutionStatistical[]> {
    const key = `${iid}-${metricId}-${dateRange.startDate.toISOString().split('T')[0]}-${dateRange.endDate.toISOString().split('T')[0]}`;
    let params = new HttpParams();
    if (currentProduct !== undefined && currentProduct !== null) {
      params = params.set('currentProduct', currentProduct);
    }
    if (!this.cache.has(key)) {
      this.cache.set(key, this.apiService.get<InstitutionStatistical[]>(`/api/${iid}/metric/statistical/${metricId}/${dateRange.startDate.toISOString().split('T')[0]}/${dateRange.endDate.toISOString().split('T')[0]}`,
        { params }
      )
        .pipe(shareReplay(1)));
    }
    return this.cache.get(key) ?? of([]);
  }

  public getGroupQuarterlySummary(iid: number, reportDate: Date, groupType: number, groupId: string): Observable<QuarterlyGroup[]> | undefined {
    const key = `${iid}-${reportDate.toISOString().split('T')[0]}-${groupType}-${groupId}`;
    if (!this.cache.has(key)) {
      this.cache.set(key, this.apiService.get<QuarterlyGroup[]>(`/api/${iid}/metric/quarterly/${reportDate.toISOString().split('T')[0]}/${groupType}/${groupId}`)
        .pipe(shareReplay(1)));
    }
    return this.cache.get(key);
  }

  public getInstitutionQuarterlySummary(iid: number, reportDate: Date, peerIids: number[]): Observable<QuarterlyInstitution[]> | undefined {
    const key = `${iid}-${reportDate.toISOString().split('T')[0]}-${peerIids.join(',')}`;
    if (!this.cache.has(key)) {
      this.cache.set(key, this.apiService.get<QuarterlyInstitution[]>(`/api/${iid}/metric/quarterly/${reportDate.toISOString().split('T')[0]}/${peerIids.join(',')}`)
        .pipe(shareReplay(1)));
    }
    return this.cache.get(key);
  }

  public getAggregateMetricRanks(iid: number, metricId: string, reportDate: Date, groupId: string): Observable<QuarterlyGroupDetail[]> | undefined {
    const key = `${iid}-${metricId}-${reportDate.toISOString().split('T')[0]}-${groupId}`;
    if (!this.cache.has(key)) {
      this.cache.set(key, this.apiService.get<QuarterlyGroupDetail[]>(`/api/${iid}/metric/aggregate/${metricId}/${reportDate.toISOString().split('T')[0]}/${groupId}`)
        .pipe(shareReplay(1)));
    }
    return this.cache.get(key);
  }
}
